import { Component, OnInit, OnDestroy, OnChanges, Input, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Waybill } from '../../../interfaces/Waybills.interfaces';
import { Carriers } from '../../../interfaces/Carriers.interfaces';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { ShipmentManagementService } from '../../../services/shipment-management.service';
import { NotificationService } from '../../../services/notification.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { TourRouting } from '../../../interfaces/TourRouting.interfaces';
import { ErrorHelperService } from '../../../services/error-helper.service';

@Component({
  selector: 'app-waybill-complete-popup',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './waybill-complete-popup.component.html',
  styleUrl: './waybill-complete-popup.component.css'
})
export class WaybillCompletePopupComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() visible: boolean = false;
  @Input() waybill: Waybill | null = null;
  @Input() carriers: Carriers[] = [];
  @Input() zones: string[] = [];
  @Input() routings: TourRouting[] = [];
  @Input() isLoadingZones: boolean = false;
  @Output() closed = new EventEmitter<void>();
  @Output() completed = new EventEmitter<any>();

  @ViewChild('estimatedDateInput') estimatedDateInput!: ElementRef<HTMLInputElement>;

  waybillForm: FormGroup;
  isSubmitting: boolean = false;
  isDropdownOpen: boolean = false;
  isZonesDropdownOpen: boolean = false;
  selectedEstimatedDate: string = ''; // Add this property for date handling
  private flatpickrInstance: FlatpickrInstance | null = null;
  
  // Search filters for dropdowns
  carrierSearchTerm: string = '';
  zoneSearchTerm: string = '';

  constructor(
    private fb: FormBuilder,
    private shipmentManagementService: ShipmentManagementService,
    private notificationService: NotificationService,
    private errorHelperService: ErrorHelperService
  ) {
    this.waybillForm = this.fb.group({
      waybillCarrierCode: ['', Validators.required],
      waybillEstimatedDeliveryDate: ['', Validators.required], // Required
      waybillDeliveryMethod: [''],
      waybillDeliveryType: ['', Validators.required], // Zone selection - required
      waybillEstimatedDeliveryTime: ['', Validators.required] // Required
    });
  }

  ngOnInit(): void {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.closeDropdownOnOutsideClick.bind(this));
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.closeDropdownOnOutsideClick.bind(this));
    this.destroyFlatpickr();
  }

  ngAfterViewInit(): void {
    // Only initialize if popup is visible
    if (this.visible) {
      this.initializeFlatpickr();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['waybill'] && this.waybill) {
      this.updateFormWithWaybillData();
    }
    
    // Reset form when popup becomes visible to ensure clean state
    if (changes['visible']) {
      if (this.visible) {
        // When popup opens, reset first then populate with waybill data
        this.resetForm();
        if (this.waybill) {
          this.updateFormWithWaybillData();
        }
        this.updateCarrierAvailability();
        // Always recreate flatpickr when popup opens to ensure proper positioning
        setTimeout(() => {
          this.destroyFlatpickr();
          this.initializeFlatpickr();
        }, 150); // Increased timeout to ensure DOM is ready
      } else {
        // When popup closes, destroy flatpickr
        this.destroyFlatpickr();
      }
    }
  }

  private updateFormWithWaybillData(): void {
    if (this.waybill) {
      // Convert date to DD/MM/YYYY format if needed
      const formattedDate = this.waybill.waybillEstimatedDeliveryDate ? 
        this.convertToDisplayFormat(this.waybill.waybillEstimatedDeliveryDate) : '';
      
      // Update selectedEstimatedDate for the date/time conversion logic
      this.selectedEstimatedDate = formattedDate;
      
      this.waybillForm.patchValue({
        waybillCarrierCode: this.waybill.waybillCarrierCode || '',
        waybillEstimatedDeliveryDate: formattedDate,
        waybillDeliveryMethod: this.waybill.waybillDeliveryMethod || '',
        waybillDeliveryType: this.waybill.waybillDeliveryType || '', // Zone
        waybillEstimatedDeliveryTime: this.extractTimeFromDate(this.waybill.waybillEstimatedDeliveryDate)
      });

      this.updateCarrierAvailability();
    }
  }

  close(): void {
    this.resetForm();
    this.closed.emit();
    this.isDropdownOpen = false;
  }

  onOverlayClick(event: Event): void {
    // Ferma la propagazione per evitare che l'evento raggiunga popup sottostanti
    event.stopPropagation();
    this.close();
  }

  private resetForm(): void {
    this.waybillForm.reset();
    this.waybillForm.patchValue({
      waybillCarrierCode: '',
      waybillEstimatedDeliveryDate: '',
      waybillDeliveryMethod: '',
      waybillDeliveryType: '',
      waybillEstimatedDeliveryTime: ''
    });
    // Reset form validation state
    this.waybillForm.markAsUntouched();
    this.waybillForm.markAsPristine();
    // Reset submission state
    this.isSubmitting = false;
    // Reset selected date
    this.selectedEstimatedDate = '';
    
    // Properly destroy and clear flatpickr instance
    this.destroyFlatpickr();

    this.waybillForm.get('waybillCarrierCode')?.disable({ emitEvent: false });
  }

  private destroyFlatpickr(): void {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy();
      this.flatpickrInstance = null;
    }
  }

  onSubmit(): void {
    if (this.waybillForm.valid && !this.isSubmitting && this.waybill) {
      this.isSubmitting = true;
      
      const formData = this.waybillForm.value;
      
      // Usare la data dal form per la data
      // Combinare data e ora in estimatedeliverydate utilizzando il formato ISO standard
      if (formData.waybillEstimatedDeliveryDate) {
        const estimatedDeliveryDate = this.convertDateTimeToISO(
          formData.waybillEstimatedDeliveryDate, 
          formData.waybillEstimatedDeliveryTime
        );
        
        // Validazione del risultato della conversione
        if (estimatedDeliveryDate) {
          formData.waybillEstimatedDeliveryDate = estimatedDeliveryDate;
        } else {
          console.error('Failed to convert date/time, skipping waybillEstimatedDeliveryDate');
          this.notificationService.error('Errore nel formato della data di consegna stimata');
          this.isSubmitting = false;
          return;
        }
      }
      
      // Rimuovere i campi temporanei prima dell'invio
      delete formData.waybillEstimatedDeliveryTime;


      
      // Preparare i dati della bolla con tutti i parametri necessari per il backend
      const waybillData = {
        ...formData,
        waybillId: this.waybill.waybillId,
        waybillCode: this.waybill.waybillCode, // Required for backend update
        waybillClientCode: this.waybill.waybillClientCode, // Required for hour range check
        waybillDate: this.waybill.waybillDate,
        waybillOriginLocation: this.waybill.waybillOriginLocation,
        waybillDestinationLocation: this.waybill.waybillDestinationLocation,
        waybillStatus: this.waybill.waybillStatus,
        waybillShipmentCode: this.waybill.waybillShipmentCode, // Include shipment code
        waybillEstimatedDeliveryDate: formData.waybillEstimatedDeliveryDate,
        waybillDeliveryMethod: this.routings.filter(r => r.tourRoutingCode == formData.waybillDeliveryType).length > 0 ? this.routings.filter(r => r.tourRoutingCode == formData.waybillDeliveryType).map(r => r.tourRoutingTransportMode)[0] : ''
      };

      // Log per debug - confronto formati date e verifica dati per backend
      console.log('Final waybill data being sent to /update endpoint:', {
        // Campi richiesti dal backend
        waybillCode: waybillData.waybillCode,
        waybillClientCode: waybillData.waybillClientCode,
        waybillCarrierCode: waybillData.waybillCarrierCode,
        waybillDeliveryType: waybillData.waybillDeliveryType,
        waybillDeliveryMethod: waybillData.waybillDeliveryMethod,
        waybillEstimatedDeliveryDate: waybillData.waybillEstimatedDeliveryDate,
        // Debug info
        selectedDate: formData.waybillEstimatedDeliveryDate,
        selectedTime: this.waybillForm.get('waybillEstimatedDeliveryTime')?.value,
        formatComparison: {
          estimated: typeof waybillData.waybillEstimatedDeliveryDate,
          waybill: typeof this.waybill.waybillDate,
          estimatedValue: waybillData.waybillEstimatedDeliveryDate,
          waybillValue: this.waybill.waybillDate
        },
        // Dati completi
        completeData: waybillData
      });

      // Chiamata all'endpoint /update del backend
      this.shipmentManagementService.updateWaybill(waybillData)
        .subscribe({
            next: (response) => {
                    console.log('Waybill updated successfully:', response);

                                // Show success message
                                this.notificationService.success('Bolla aggiornata con successo');

                                // Emit completion event with updated data for parent component
                                this.completed.emit({
                                  success: true,
                                  data: waybillData,
                                  response: response
                                });

                                // Reset form after successful submission
                                setTimeout(() => {
                                  this.resetForm();
                                  this.close();
                                }, 100);
                  },
                  error: (error) => {
                    console.error('Error updating waybill:', error);
                    const message = this.errorHelperService.extractBackendMessage(error);
                    this.notificationService.error(`Errore: ${message}`);
                    this.isSubmitting = false;
                    setTimeout(() => {
                                  this.resetForm();
                                  this.close();
                                }, 100);
                  }
              });
    }
  }

  // Carrier dropdown methods
  toggleDropdown(): void {

    if (this.isCarrierDisabled()) {
      this.isDropdownOpen = false;
      return;
    }
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.isZonesDropdownOpen = false;
      this.carrierSearchTerm = ''; // Reset search when opening
    }
  }

  selectCarrier(carrier: Carriers): void {
    this.waybillForm.patchValue({
      waybillCarrierCode: carrier.carrierCode
    });
    this.isDropdownOpen = false;
    this.isZonesDropdownOpen = false;
  }

  getSelectedCarrier(): Carriers | undefined {
    const carrierCode = this.waybillForm.get('waybillCarrierCode')?.value;
    return this.carriers.find(c => c.carrierCode === carrierCode);
  }

  private closeDropdownOnOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-carrier-dropdown')) {
      this.isDropdownOpen = false;
    }
    if (!target.closest('.custom-zones-dropdown')) {
      this.isZonesDropdownOpen = false;
    }
  }
    toggleZonesDropdown(): void {
    this.isZonesDropdownOpen = !this.isZonesDropdownOpen;
    if (this.isZonesDropdownOpen) {
      this.isDropdownOpen = false;
      this.zoneSearchTerm = ''; // Reset search when opening
    }
  }

  selectZone(zone: string): void {
    this.waybillForm.patchValue({
      waybillDeliveryType: zone
    });
    this.isZonesDropdownOpen = false;
    this.isDropdownOpen = false;

    const isDirettaBo = zone === 'DIRETTA BO';
    const isBigOrder = this.waybill?.waybillDeliveryMethod === 'BIG_ORDER';

    const matchedRouting = this.routings.find(r => r.tourRoutingCode === zone);
    if (matchedRouting && matchedRouting.tourRoutingCarrierCode) {
      const autoCarrierCode = matchedRouting.tourRoutingCarrierCode;
      const carrierExists = this.carriers?.some(c => c.carrierCode === autoCarrierCode);
      if (carrierExists) {
        this.waybillForm.patchValue({
          waybillCarrierCode: autoCarrierCode
        });
      } else {
        this.waybillForm.patchValue({ waybillCarrierCode: '' });
      }
    } else {
      this.waybillForm.patchValue({ waybillCarrierCode: '' });
    }

    // Logic: 
    // - If waybill is NOT BIG_ORDER: always enable carrier (no restrictions)
    // - If waybill is BIG_ORDER: enable carrier only if routing is DIRETTA BO
    let shouldEnable: boolean;
    if (!isBigOrder) {
      // Not BIG_ORDER: always enable
      shouldEnable = true;
    } else {
      // BIG_ORDER: enable only if DIRETTA BO
      shouldEnable = isDirettaBo;
    }

    if (shouldEnable) {
      this.waybillForm.get('waybillCarrierCode')?.enable({ emitEvent: false });
    } else {
      this.waybillForm.get('waybillCarrierCode')?.disable({ emitEvent: false });
    }
  }

  getSelectedZone(): string | null {
    return this.waybillForm.get('waybillDeliveryType')?.value || null;
  }

  // Filter methods for search
  getFilteredCarriers(): Carriers[] {
    if (!this.carrierSearchTerm.trim()) {
      return this.carriers;
    }
    const searchLower = this.carrierSearchTerm.toLowerCase();
    return this.carriers.filter(carrier => 
      carrier.carrierName?.toLowerCase().includes(searchLower) ||
      carrier.carrierCode?.toLowerCase().includes(searchLower) ||
      carrier.carrierVatNumber?.toLowerCase().includes(searchLower) ||
      carrier.carrierInternalId?.toString().includes(searchLower)
    );
  }

  // Solo i tour attivi: con un tour INACTIVE il backend risponde error.tour.routing.inactive.
  // zones arriva da GET /api/anagraphics/tour-routings, che restituisce tutti i tour con lo stato
  private getActiveZones(): string[] {
    const activeCodes = new Set(
      this.routings
        .filter(r => r.tourRoutingStatus !== 'INACTIVE')
        .map(r => r.tourRoutingCode)
    );
    return this.zones.filter(zone => activeCodes.has(zone));
  }

  getFilteredZones(): string[] {
    const activeZones = this.getActiveZones();
    if (!this.zoneSearchTerm.trim()) {
      return activeZones;
    }
    const searchLower = this.zoneSearchTerm.toLowerCase();
    return activeZones.filter(zone =>
      zone?.toLowerCase().includes(searchLower)
    );
  }

  clearCarrierSearch(): void {
    this.carrierSearchTerm = '';
  }

  clearZoneSearch(): void {
    this.zoneSearchTerm = '';
  }

  // Form validation methods
  isFieldInvalid(fieldName: string): boolean {
    const field = this.waybillForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  getFieldError(fieldName: string): string {
    const field = this.waybillForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        if (fieldName === 'waybillDeliveryType') {
          return 'Instradamento è obbligatorio';
        }
        if (fieldName === 'waybillEstimatedDeliveryDate') {
          return 'Data Consegna Stimata è obbligatoria';
        }
        if (fieldName === 'waybillEstimatedDeliveryTime') {
          return 'Ora Consegna Stimata è obbligatoria';
        }
        return `${fieldName} è obbligatorio`;
      }
    }
    return '';
  }

  // Date and time handling
  getFormattedDate(): string {
    const date = this.waybillForm.get('waybillEstimatedDeliveryDate')?.value;
    return date || '';
  }

  isCarrierDisabled(): boolean {
    const routingValue = this.waybillForm.get('waybillDeliveryType')?.value;
    const isDirettaBo = routingValue === 'DIRETTA BO';
    const isBigOrder = this.waybill?.waybillDeliveryMethod === 'BIG_ORDER';
    
    // Logic: 
    // - If waybill is NOT BIG_ORDER: always enable carrier (no restrictions)
    // - If waybill is BIG_ORDER: enable carrier only if routing is DIRETTA BO
    let shouldEnable: boolean;
    if (!isBigOrder) {
      // Not BIG_ORDER: always enable
      shouldEnable = true;
    } else {
      // BIG_ORDER: enable only if DIRETTA BO
      shouldEnable = isDirettaBo;
    }
    
    const control = this.waybillForm.get('waybillCarrierCode');
    if (shouldEnable) {
      if (control?.disabled) control.enable({ emitEvent: false });
    } else {
      if (control?.enabled) control.disable({ emitEvent: false });
    }
    return !shouldEnable;
  }

  private updateCarrierAvailability(): void {
    const routingValue = this.waybillForm.get('waybillDeliveryType')?.value;
    const isDirettaBo = routingValue === 'DIRETTA BO';
    const isBigOrder = this.waybill?.waybillDeliveryMethod === 'BIG_ORDER';
    
    // Logic: 
    // - If waybill is NOT BIG_ORDER: always enable carrier (no restrictions)
    // - If waybill is BIG_ORDER: enable carrier only if routing is DIRETTA BO
    let shouldEnable: boolean;
    if (!isBigOrder) {
      // Not BIG_ORDER: always enable
      shouldEnable = true;
    } else {
      // BIG_ORDER: enable only if DIRETTA BO
      shouldEnable = isDirettaBo;
    }
    
    const carrierControl = this.waybillForm.get('waybillCarrierCode');
    if (shouldEnable) {
      carrierControl?.enable({ emitEvent: false });
    } else {
      carrierControl?.disable({ emitEvent: false });
      this.waybillForm.patchValue({ waybillCarrierCode: '' });
    }
  }

  private extractTimeFromDate(dateString?: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toTimeString().slice(0, 5); // HH:MM format
      }
    } catch (error) {
      console.error('Error extracting time from date:', error);
    }
    return '';
  }

  private initializeFlatpickr(): void {
    // Ensure any existing instance is destroyed first
    this.destroyFlatpickr();
    
    if (this.estimatedDateInput && this.estimatedDateInput.nativeElement) {
      const inputElement = this.estimatedDateInput.nativeElement;
      
      // Ensure the input element is visible and in the DOM
      if (!inputElement.offsetParent) {
        console.warn('Flatpickr input element not visible, retrying...');
        setTimeout(() => this.initializeFlatpickr(), 100);
        return;
      }

      this.flatpickrInstance = flatpickr(inputElement, {
        dateFormat: 'd/m/Y',
        locale: Italian,
        minDate: 'today',
        allowInput: false,
        clickOpens: true,
        static: false, // Allow positioning relative to input
        positionElement: inputElement, // Explicitly set position element
        onChange: (selectedDates: Date[]) => {
          if (selectedDates.length > 0) {
            const date = selectedDates[0];
            const formattedDate = this.formatDateForForm(date);
            this.selectedEstimatedDate = formattedDate; // Update the separate property
            this.waybillForm.patchValue({ 
              waybillEstimatedDeliveryDate: formattedDate 
            });
            // Mark field as touched for validation
            this.waybillForm.get('waybillEstimatedDeliveryDate')?.markAsTouched();
          }
        },
        onClose: () => {
          // Mark field as touched when picker closes
          this.waybillForm.get('waybillEstimatedDeliveryDate')?.markAsTouched();
        },
        onReady: () => {
          console.log('Flatpickr initialized successfully');
        }
      });
    } else {
      console.warn('Flatpickr input element not found, retrying...');
      setTimeout(() => this.initializeFlatpickr(), 100);
    }
  }

  private formatDateForForm(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private convertToDisplayFormat(dateString: string): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return this.formatDateForForm(date);
      }
    } catch (error) {
      console.error('Error converting date to display format:', error);
    }
    return dateString; // Return original if conversion fails
  }

  openDatePicker(): void {
    if (!this.flatpickrInstance) {
      this.initializeFlatpickr();
      setTimeout(() => {
        if (this.flatpickrInstance) {
          this.flatpickrInstance.open();
        }
      }, 100);
    } else {
      this.flatpickrInstance.open();
    }
  }

  updateWaybillEstimatedDelivery(): void {
    // The form will automatically update when values change
    console.log('Estimated delivery updated');
  }

  focusTimeInput(): void {
    const timeInput = document.getElementById('waybillEstimatedDeliveryTime') as HTMLInputElement;
    if (timeInput) {
      timeInput.focus();
    }
  }

  /**
   * Utility function to convert date and time to ISO format consistent with waybillDate
   */
  private convertDateTimeToISO(dateString: string, timeString?: string): string {
    if (!dateString) return '';
    
    // Debug log dei valori in input
    console.log('convertDateTimeToISO input:', { dateString, timeString });
    
    let year: number, month: number, day: number;
    
    // Gestione di diversi formati di data
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      // Formato DD/MM/YYYY (dal datepicker italiano)
      const [dayStr, monthStr, yearStr] = dateString.split('/');
      day = parseInt(dayStr);
      month = parseInt(monthStr);
      year = parseInt(yearStr);
    } else if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Formato YYYY-MM-DD (standard ISO)
      const [yearStr, monthStr, dayStr] = dateString.split('-');
      year = parseInt(yearStr);
      month = parseInt(monthStr);
      day = parseInt(dayStr);
    } else {
      console.error('Invalid date format:', dateString, 'Expected DD/MM/YYYY or YYYY-MM-DD');
      return '';
    }
    
    // Validazione dei valori numerici
    if (isNaN(year) || isNaN(month) || isNaN(day) || 
        year < 1900 || year > 2100 || 
        month < 1 || month > 12 || 
        day < 1 || day > 31) {
      console.error('Invalid date values:', { year, month, day });
      return '';
    }
    
    if (timeString && timeString.trim()) {
      // Validazione del formato ora
      if (!timeString.match(/^\d{2}:\d{2}$/)) {
        console.error('Invalid time format:', timeString);
        return '';
      }
      
      const [hours, minutes] = timeString.split(':').map(Number);
      
      // Validazione dei valori di tempo
      if (isNaN(hours) || isNaN(minutes) || 
          hours < 0 || hours > 23 || 
          minutes < 0 || minutes > 59) {
        console.error('Invalid time values:', { hours, minutes });
        return '';
      }
      
      const combinedDateTime = new Date(year, month - 1, day, hours, minutes, 0, 0);
      
      // Validazione della data creata
      if (isNaN(combinedDateTime.getTime())) {
        console.error('Invalid combined date:', { year, month, day, hours, minutes });
        return '';
      }
      
      console.log('Created combined date:', combinedDateTime.toISOString());
      return combinedDateTime.toISOString();
    } else {
      // Solo data, imposta ore a mezzanotte (come fa waybillDate)
      const dateOnly = new Date(year, month - 1, day, 0, 0, 0, 0);
      
      // Validazione della data creata
      if (isNaN(dateOnly.getTime())) {
        console.error('Invalid date only:', { year, month, day });
        return '';
      }
      
      console.log('Created date only:', dateOnly.toISOString());
      return dateOnly.toISOString();
    }
  }
}
